
import { getSuburb } from '@/api';
import Button from '@/components/UI/button/Button.vue';
import LookupSuburb from '@/components/UI/lookups/LookupSuburb.vue';
import { AddressBookData } from '@/models/addressBook';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import Sortable from 'sortablejs';

@Component({
  components: {
    Button,
    LookupSuburb,
  },
})
export default class AddressesTable extends Vue {
  @Prop({ type: Array, required: true }) public addresses: AddressBookData[];
  @Prop({ type: String, required: true, default: 'My addresses' }) public title: string;
  @Prop({ type: Boolean, required: false, default: true }) public editable: boolean;
  @Prop({ type: Number, required: true }) public addressBookId: number;
  @Prop({ type: String, required: false, default: '' }) public context: string;
  @Prop({ type: Boolean, required: false, default: false }) public loading: boolean;
  @Prop({ type: String, required: false }) public bookCustomerCode: string;

  private autocompleteSuburb = getSuburb;

  private itemsPerPage = 10;
  private pageNumber = 1;

  private headers = [
    { text: 'Sort', align: 'start', sortable: false, value: 'sort' },
    {
      text: 'Stop name',
      align: 'start',
      sortable: false,
      value: 'name',
    },
    { text: 'Street address', align: 'start', sortable: false, value: 'street' },
    { text: 'Suburb', align: 'start', sortable: false, value: 'suburb' },
    { text: 'State', align: 'start', sortable: false, value: 'state' },
    { text: 'Postcode', align: 'start', sortable: false, value: 'postcode' },
    { text: 'Actions', align: 'start', sortable: false, value: 'actions' },
  ];

  private addressesFormated: AddressBookData[] = [];
  private dialog = false;
  private dialogDelete = false;
  private editedIndex = -1;

  private editedItem: AddressBookData = {
    name: '',
    street: '',
    suburb: '',
    suburbId: -1,
    state: '',
    postcode: '',
  };
  private defaultItem = {
    name: '',
    street: '',
    suburb: '',
    suburbId: -1,
    state: '',
    postcode: '',
    line1: '',
    line2: '',
    note_phone: '',
    note_text: '',
    note_name: '',
  };

  private editItem(item: AddressBookData): void {
    this.editedIndex = this.addressesFormated.indexOf(item);
    this.editedItem = Object.assign({}, item);
    this.changeSuburbLookup(0, item);
    this.dialog = true;
    if (this.editedIndex === -1) {
      const form = this.$refs.form as HTMLFormElement;
      if (form) {
        form.reset();
      }
    }
  }

  private deleteItem(item: AddressBookData): void {
    this.editedIndex = this.addresses.indexOf(item);
    this.editedItem = Object.assign({}, item);
    this.dialogDelete = true;
  }

  private setDefaultAddress(item: AddressBookData): void {
    this.$emit('setDefaultAddress', item.id, this.context, this.bookCustomerCode);
  }

  private deleteItemConfirm(item): void {
    this.$emit('removeAddress', item.id, this.context);
    this.closeDelete();
  }

  private close(): void {
    this.dialog = false;
    this.$nextTick(() => {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    });
  }

  private closeDelete(): void {
    this.dialogDelete = false;
    this.$nextTick(() => {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    });
  }

  private async save(): Promise<void> {
    const form = this.$refs.form as HTMLFormElement;
    if (form.validate()) {
      if (this.editedIndex === -1) {
        await this.$emit('addAddress', this.editedItem, this.addressBookId, this.context);
      } else {
        await this.$emit('editAddress', this.editedItem, this.addressBookId, this.context);
      }
      this.editedItem = Object.assign({}, this.defaultItem);
      this.close();
    }
  }

  //   SUBURB LOOKUP
  private changeSuburbLookup(stop, item) {
    this.editedItem = {
      ...this.editedItem,
      suburb: item.suburb || item.name || '',
      suburbId: item.suburbId || item.id || 0,
      state: item.state || '',
      postcode: item.postcode || '',
    };
  }

  private clearSuburbLookup() {
    this.editedItem = {
      ...this.editedItem,
      suburb: '',
      suburbId: -1,
      state: '',
      postcode: '',
    };
  }

  private formatAddresses(addresses): void {
    this.addressesFormated = [];
    addresses.map(address => {
      this.addressesFormated.push({
        name: address.name,
        line1: address.line1,
        line2: address.line2,
        line3: address.line3,
        street: `${address.line1 ? address.line1 : ''} ${address.line2 ? address.line2 : ''} ${
          address.line3 ? address.line3 : ''
        }`,
        suburb: address.suburb ? address.suburb.name : '',
        suburbId: address.suburb ? address.suburb.id : -1,
        state: address.suburb ? address.suburb.state : '',
        postcode: address.suburb ? address.suburb.postcode : '',
        id: address.id,
        note_name: address.note_name || '',
        note_phone: address.note_phone || '',
        note_text: address.note_text || '',
        is_default: address.is_default,
      });
    });
  }

  private mounted(): void {
    this.formatAddresses(this.addresses);
    // Removing Actions column if address book is not editable
    if (!this.editable) {
      this.headers = this.headers.filter(header => {
        return header.text !== 'Actions';
      });
    }
    // Adding sorting
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const sortableTable = new Sortable((this.$refs.sortableTable as Vue).$el.getElementsByTagName('tbody')[0], {
      draggable: 'tr',
      handle: '.sortHandle',
      onEnd: this.dragReorder,
    });
  }

  private getItemPerPage(val) {
    this.itemsPerPage = val;
  }

  private getPageNum(val) {
    this.pageNumber = val;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  private dragReorder({ oldIndex, newIndex, item }) {
    const itemId = JSON.parse(item.getElementsByClassName('sortHandle')[0].getAttribute('attr-id'));
    const newGlobalIndex = (this.pageNumber - 1) * this.itemsPerPage + newIndex;
    this.$emit('reorderAddress', itemId, newGlobalIndex, this.context);
  }

  @Watch('addresses')
  private changeAddresses() {
    this.formatAddresses(this.addresses);
  }
}
